/**
 * Comparaison sémantique de deux versions d'un document d'éditeur (ADR-146 §5.3 : « +2 composants, −1
 * champ, 3 propriétés modifiées ») : le métier découpe chaque version en éléments nommés (bloc, champ,
 * page…) ; le socle dit ce qui est ajouté, retiré ou modifié, en mots, sans chemin ni JSON. Générique.
 */

export interface DiffItem {
  /** Identité stable de l'élément dans le document (jamais montrée). */
  key: string;
  /** Sorte d'élément (`block`, `field`…), pour le décompte. */
  kind: string;
  /** Nom en langage courant. */
  label: string;
  /** Où il se trouve, en mots (« dans Contenu »), facultatif. */
  where?: string;
  value: unknown;
}

export interface DiffLine {
  sign: '+' | '−' | '~';
  kind: string;
  text: string;
}

export interface SemanticDiff {
  lines: DiffLine[];
  added: Record<string, number>;
  removed: Record<string, number>;
  /** Nombre de réglages modifiés dans les éléments présents des deux côtés. */
  changedSettings: number;
}

const isRecord = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value);

function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (isRecord(value)) return `{${Object.keys(value).sort().filter((key) => value[key] !== undefined).map((key) => `${JSON.stringify(key)}:${canonical(value[key])}`).join(',')}}`;
  return JSON.stringify(value ?? null);
}

/** Nombre de réglages (feuilles) qui diffèrent entre deux valeurs. */
export function changedLeaves(base: unknown, draft: unknown): number {
  if (canonical(base) === canonical(draft)) return 0;
  if (Array.isArray(base) && Array.isArray(draft) && base.length === draft.length) return base.reduce((sum: number, item, index) => sum + changedLeaves(item, draft[index]), 0);
  if (isRecord(base) && isRecord(draft)) {
    const keys = new Set([...Object.keys(base), ...Object.keys(draft)]);
    let count = 0;
    for (const key of keys) count += changedLeaves(base[key], draft[key]);
    return count;
  }
  return 1;
}

export function semanticDiff(base: readonly DiffItem[], draft: readonly DiffItem[], nouns: Readonly<Record<string, string>>): SemanticDiff {
  const before = new Map(base.map((item) => [`${item.kind}:${item.key}`, item] as const));
  const after = new Map(draft.map((item) => [`${item.kind}:${item.key}`, item] as const));
  const lines: DiffLine[] = [];
  const added: Record<string, number> = {};
  const removed: Record<string, number> = {};
  let changedSettings = 0;
  const noun = (kind: string): string => nouns[kind] ?? 'Élément';
  for (const [id, item] of after) {
    const previous = before.get(id);
    if (previous === undefined) {
      added[item.kind] = (added[item.kind] ?? 0) + 1;
      lines.push({ sign: '+', kind: item.kind, text: `${noun(item.kind)} « ${item.label} »${item.where === undefined ? '' : ` ${item.where}`}` });
      continue;
    }
    const count = changedLeaves(previous.value, item.value);
    if (count > 0) {
      changedSettings += count;
      const renamed = previous.label !== item.label ? ` (ancien nom : « ${previous.label} »)` : '';
      lines.push({ sign: '~', kind: item.kind, text: `${noun(item.kind)} « ${item.label} »${renamed} : ${count} réglage${count > 1 ? 's' : ''} modifié${count > 1 ? 's' : ''}` });
    }
  }
  for (const [id, item] of before) {
    if (after.has(id)) continue;
    removed[item.kind] = (removed[item.kind] ?? 0) + 1;
    lines.push({ sign: '−', kind: item.kind, text: `${noun(item.kind)} « ${item.label} »${item.where === undefined ? '' : ` ${item.where}`}` });
  }
  const order = { '+': 0, '~': 1, '−': 2 } as const;
  lines.sort((a, b) => order[a.sign] - order[b.sign]);
  return { lines, added, removed, changedSettings };
}

/** Une ligne de synthèse : « +2 blocs · −1 champ · 3 réglages modifiés » ; vide : « Aucune différence ». */
export function diffHeadline(diff: SemanticDiff, plurals: Readonly<Record<string, readonly [string, string]>>): string {
  const word = (kind: string, count: number): string => {
    const forms = plurals[kind] ?? ['élément', 'éléments'];
    return count > 1 ? forms[1] : forms[0];
  };
  const parts = [
    ...Object.entries(diff.added).map(([kind, count]) => `+${count} ${word(kind, count)}`),
    ...Object.entries(diff.removed).map(([kind, count]) => `−${count} ${word(kind, count)}`),
  ];
  if (diff.changedSettings > 0) parts.push(`${diff.changedSettings} réglage${diff.changedSettings > 1 ? 's' : ''} modifié${diff.changedSettings > 1 ? 's' : ''}`);
  return parts.length === 0 ? 'Aucune différence' : parts.join(' · ');
}
